import { useEffect, useRef, useState } from "react";
import { SIM } from "@/constants/testIds";
import { fmtMoney, fmtMoneyDetail, fmtNumber } from "@/lib/simulator";
import AnimatedNumber from "@/components/simulator/AnimatedNumber";
import GrowthChart from "@/components/simulator/TimeSeriesChart";
import RankTrack from "@/components/simulator/RankTrack";
import BonusCard from "@/components/simulator/BonusCard";
import { Coins, Sparkle, Users, Trophy, TrendUp } from "@phosphor-icons/react";
import { useI18n } from "@/i18n";

/**
 * Always-visible results panel. Shows:
 *  - Hero: Team Volume -> Your Commissions + Shares
 *  - Revenue-vs-team-size growth chart (no time axis)
 *  - Money-by-engine breakdown
 *  - Shares + Team & Rank snapshot
 */
export default function ResultsPanel({ result, empty }) {
  const { t } = useI18n();
  // Pulse the "You earn" number when it jumps upward (e.g. user advances a step).
  // Declared before any early return so hook order stays stable.
  const currentCash = result?.totals?.totalCash ?? 0;
  const prevCashRef = useRef(currentCash);
  const [pulseKey, setPulseKey] = useState(0);
  useEffect(() => {
    const prev = prevCashRef.current;
    const jumped = currentCash > prev * 1.05 + 1; // > ~5% (skip slider jitter)
    prevCashRef.current = currentCash;
    if (jumped) setPulseKey((k) => k + 1);
  }, [currentCash]);

  if (empty) {
    return (
      <div
        data-testid={SIM.resultsPanel}
        className="glass rounded-3xl p-8 md:p-10 text-center h-full flex flex-col items-center justify-center min-h-[480px]"
      >
        <div className="h-14 w-14 rounded-2xl bg-white/[0.03] ring-1 ring-white/[0.08] flex items-center justify-center mb-4">
          <Sparkle weight="duotone" className="h-7 w-7 text-pink-300" />
        </div>
        <div className="font-display text-xl font-semibold text-white">
          {t("empty.title")}
        </div>
        <div className="text-sm text-slate-400 mt-2 max-w-xs">
          {t("empty.sub")}
        </div>
      </div>
    );
  }

  const teamSize = result.tree.reduce((a, l) => a + l.members, 0);
  const orgVolume = result.tree.reduce((a, l) => a + l.volume, 0);
  const commissionPct =
    orgVolume > 0 ? (result.totals.totalCash / orgVolume) * 100 : 0;

  return (
    <div data-testid={SIM.resultsPanel} className="space-y-5">
      {/* HERO: Revenue -> Commission + Shares */}
      <div className="relative fade-up">
        <div
          className="absolute -inset-4 rounded-3xl blur-3xl opacity-30 pointer-events-none"
          style={{ background: "radial-gradient(closest-side,#EC4899,transparent 70%)" }}
        />
        <div className="relative glass rounded-3xl p-6 md:p-7 grad-border">
          <div className="text-[10px] uppercase tracking-[0.22em] text-slate-400">
            {t("hero.projection")}
          </div>

          {/* Primary metric: You earn (dominant) */}
          <div className="mt-4">
            <div className="flex items-center gap-1.5 text-[10px] uppercase tracking-widest text-slate-400">
              <Coins weight="bold" className="h-3 w-3" /> {t("hero.youEarn")}
            </div>
            <div
              key={pulseKey}
              data-testid={SIM.heroCommission}
              className="earnings-pulse mt-1 font-mono font-semibold grad-text leading-none text-5xl sm:text-6xl md:text-7xl"
            >
              <AnimatedNumber
                value={result.totals.totalCash}
                formatter={fmtMoney}
              />
            </div>
            <div className="text-xs text-slate-500 mt-2">
              {t("hero.commissionsSub", { pct: commissionPct.toFixed(2) })}
            </div>
          </div>

          {/* Secondary metrics: team volume + team payout + shares */}
          <div className="mt-5 grid grid-cols-1 md:grid-cols-3 gap-3">
            <div className="rounded-xl bg-white/[0.03] border border-white/[0.06] px-3 py-2.5">
              <div className="flex items-center gap-1.5 text-[10px] uppercase tracking-widest text-slate-400">
                <Users weight="bold" className="h-3 w-3" /> {t("hero.teamGenerates")}
              </div>
              <div
                data-testid={SIM.heroVolume}
                className="mt-1 font-mono text-lg font-semibold text-slate-100"
              >
                <AnimatedNumber value={orgVolume} formatter={fmtMoney} />
              </div>
              <div className="text-[10px] text-slate-500 mt-0.5">
                {t("hero.teamGeneratesSub")}
              </div>
            </div>

            <div className="rounded-xl bg-white/[0.03] border border-white/[0.06] px-3 py-2.5">
              <div className="flex items-center gap-1.5 text-[10px] uppercase tracking-widest text-slate-400">
                <Coins weight="bold" className="h-3 w-3" /> {t("hero.teamPayout")}
              </div>
              <div
                data-testid="sim-hero-team-payout"
                className="mt-1 font-mono text-lg font-semibold text-transparent bg-clip-text bg-gradient-to-r from-emerald-300 to-teal-200"
              >
                <AnimatedNumber
                  value={result.totals.teamCash || 0}
                  formatter={fmtMoney}
                />
              </div>
              <div className="text-[10px] text-slate-500 mt-0.5">
                {t("hero.teamPayoutSub")}
              </div>
            </div>

            <div className="rounded-xl bg-white/[0.03] border border-white/[0.06] px-3 py-2.5">
              <div className="flex items-center gap-1.5 text-[10px] uppercase tracking-widest text-slate-400">
                <Sparkle weight="bold" className="h-3 w-3" /> {t("hero.plusShares")}
              </div>
              <div
                data-testid={SIM.heroShares}
                className="mt-1 font-mono text-lg font-semibold text-transparent bg-clip-text bg-gradient-to-r from-sky-300 to-cyan-200"
              >
                <AnimatedNumber
                  value={result.shares.totalShares}
                  formatter={fmtNumber}
                />
              </div>
              <div className="text-[10px] text-slate-500 mt-0.5">
                {t("hero.plusSharesSub", {
                  start: fmtNumber(result.shares.startingShares),
                  earned: fmtNumber(result.shares.earnedShares),
                })}
              </div>
            </div>
          </div>

          {/* Sub-metrics */}
          <div className="mt-6 grid grid-cols-2 md:grid-cols-4 gap-3">
            <MetricPill
              testId="sim-metric-team"
              icon={Users}
              label={t("hero.teamSize")}
              value={fmtNumber(teamSize)}
              sub={t("hero.teamSizeSub")}
              tone="pink"
            />
            <MetricPill
              testId={SIM.currentRank}
              icon={Trophy}
              label={t("hero.yourRank")}
              value={result.rank.rank}
              sub={result.rank.unlock}
              tone="purple"
            />
            <MetricPill
              testId="sim-metric-per-member"
              icon={Coins}
              label={t("hero.perMember")}
              value={
                teamSize > 0
                  ? fmtMoney(result.totals.totalCash / teamSize)
                  : "$0"
              }
              sub={t("hero.perMemberSub")}
              tone="orange"
            />
            <MetricPill
              testId="sim-metric-avg-order"
              icon={TrendUp}
              label={t("hero.avgOrder")}
              value={
                teamSize > 0 ? fmtMoney(orgVolume / teamSize) : "$0"
              }
              sub={t("hero.avgOrderSub")}
              tone="sky"
            />
          </div>

          <div className="mt-6" data-testid={SIM.rankProgress}>
            <RankTrack
              currentRank={result.rank.rank}
              nextRank={result.nextRank}
              totalQV={result.totalQV}
            />
          </div>
        </div>
      </div>

      {/* Chart */}
      <div className="glass rounded-2xl p-5 md:p-6 fade-up" data-testid={SIM.breakdownChart}>
        <div className="flex items-center justify-between mb-3">
          <div>
            <div className="text-[11px] uppercase tracking-widest text-slate-400">
              {t("chart.title")}
            </div>
            <div className="text-sm text-slate-300">
              {t("chart.sub")}
            </div>
          </div>
          <div className="text-[10px] text-slate-500 font-mono">
            {fmtNumber(teamSize)} members · {fmtMoney(orgVolume)} vol
          </div>
        </div>
        <GrowthChart curve={result.growthCurve} />
      </div>

      {/* Bonus breakdowns */}
      <div className="grid md:grid-cols-2 gap-4 fade-up" data-testid={SIM.levelChart}>
        <BonusCard
          testId={SIM.breakdownIR}
          title="Initial Ripple"
          subtitle={`Direct commissions · L1-L5 · ${result.initialRipple.unlockedTiers}/5 unlocked`}
          color="orange"
          total={result.initialRipple.total}
          rows={result.initialRipple.breakdown.map((r) => ({
            label: `${r.label} (${r.ratePct}%)`,
            sub: r.unlocked
              ? `${fmtNumber(r.members)} members · ${fmtMoney(r.volume)} vol`
              : `Locked — need R${r.level}`,
            value: r.unlocked
              ? fmtMoneyDetail(r.earnings)
              : `~${fmtMoneyDetail(r.potential)} pot.`,
            muted: !r.unlocked,
          }))}
          footer={`Package unlocks ${result.initialRipple.maxFromPackage}/5. Rank progression unlocks more.`}
        />
        <BonusCard
          testId={SIM.breakdownRW}
          title="Ripple Wave"
          subtitle={`R6+ rank commissions · ${result.rippleWave.unlockedTiers}/6 tiers unlocked`}
          color="pink"
          total={result.rippleWave.total}
          rows={result.rippleWave.breakdown.map((r) => ({
            label: `${r.label} (${r.ratePct}%) · ${r.tag}`,
            sub: r.unlocked
              ? (r.volume > 0
                ? `Fires on ${fmtMoney(r.volume)} of downline volume`
                : "Unlocked — currently capped by 3-payment rule")
              : `Locked — reach R${r.level}`,
            value: r.unlocked
              ? fmtMoneyDetail(r.earnings)
              : `~${fmtMoneyDetail(r.potential)} pot.`,
            muted: !r.unlocked || r.volume === 0,
          }))}
          footer="1% per unlocked tier on every downline order (L1–L11). Max 3 payments per order shared with Initial Ripple."
        />
        <BonusCard
          testId={SIM.breakdownDR}
          title="Deep Ripple"
          subtitle="Leadership override · L12-L20 · needs R12+"
          color="purple"
          total={result.deepRipple.total}
          rows={[
            {
              label: "G1 · R12–R14 (0.3/0.6/0.9%)",
              sub: "Entry leadership",
              value: fmtMoneyDetail(result.deepRipple.potentialByGroup.G1),
              muted: result.deepRipple.unlockedGroup !== "G1",
            },
            {
              label: "G2 · R15–R17 (0.3/0.6/0.9%)",
              sub: "Advanced leadership",
              value: fmtMoneyDetail(result.deepRipple.potentialByGroup.G2),
              muted: result.deepRipple.unlockedGroup !== "G2",
            },
            {
              label: "G3 · R18–R20 (0.3/0.6/0.9%)",
              sub: "Elite leadership",
              value: fmtMoneyDetail(result.deepRipple.potentialByGroup.G3),
              muted: result.deepRipple.unlockedGroup !== "G3",
            },
          ]}
          footer="Rate = position within your group. Fires only when the 3-slot cap has room."
        />
        <BonusCard
          testId={SIM.breakdownDM}
          title="Drop Match"
          subtitle="Shares (not cash) · L1-L3 · uncapped"
          color="sky"
          totalOverride={
            <span>
              <AnimatedNumber
                value={result.dropMatch.sharesTotal}
                formatter={fmtNumber}
              />{" "}
              <span className="text-slate-400 text-sm">shares</span>
            </span>
          }
          rows={result.dropMatch.breakdown.map((r) => ({
            label: `${r.label} (${r.ratePct}%)`,
            sub: `${fmtNumber(r.members)} members`,
            value: `${fmtNumber(r.shares)} shares`,
          }))}
          footer="Points-based participation metric applied to shares generated by each order."
        />
      </div>
    </div>
  );
}

function MetricPill({ testId, icon: Icon, label, value, sub, tone = "orange" }) {
  const toneMap = {
    orange: "text-orange-300",
    pink: "text-pink-300",
    purple: "text-purple-300",
    sky: "text-sky-300",
  };
  return (
    <div className="rounded-xl bg-white/[0.03] border border-white/[0.06] px-3 py-2.5">
      <div className="flex items-center gap-1.5 text-[10px] uppercase tracking-widest text-slate-400">
        <Icon weight="bold" className="h-3 w-3" /> {label}
      </div>
      <div
        data-testid={testId}
        className={`mt-1 font-mono text-lg font-semibold ${toneMap[tone]}`}
      >
        {value}
      </div>
      {sub && <div className="text-[10px] text-slate-500 mt-0.5">{sub}</div>}
    </div>
  );
}
